import { GetStaticProps, NextPage } from 'next';
import { useMemo } from 'react';

import SEO from '@/components/global/SEO';
import Layout from '@/components/global/Layout';
import HeroSection from '@/components/Pages/Aplicativos/HeroSection';
import Transportadoras from '@/components/global/Transportadoras';
import NovosAplicativos from '@/components/Pages/Aplicativos/NovosAplicativos';
import Pontuacao from '@/components/Pages/Aplicativos/Pontuacao';
import Integracao from '@/components/Pages/Aplicativos/Integracao';
import PrincipaisCases from '@/components/global/PrincipaisCases';
import ClientCases from '@/components/global/ClientCases';
import ReusableBottomContent from '@/components/global/ReusableBottomContent';

interface IProps {
  clientes: IClients[];
  cases: IWebAppCases[];
}

interface IClients {
  name: string;
  logo: string;
  id: number;
  services: string[];
}

interface IWebAppCases {
  id: number;
  content: string;
  type: string;
}

import { PageContainer, PageWrapper, SectionWrapper } from '@/styles/Page';
import DirectusInstance from '@/services/directusCMS';
import { ManyItems } from '@directus/sdk';
import { CMS_URL } from '@/config';
import { useClientCases } from '@/components/global/Providers/ClientCases';

const Aplicativos: NextPage<IProps> = ({ clientes, cases }) => {
  const { clientCases } = useClientCases();

  const empresas = useMemo(() => {
    return clientes.map((cliente) => {
      return {
        src: `${CMS_URL}/assets/${cliente.logo}`,
        nome: cliente.name,
      };
    });
  }, [clientes]);

  const casesParsed: IWebAppCases[] = useMemo(() => {
    return cases.map((clienteCase) => {
      return {
        id: clienteCase.id,
        content: `${CMS_URL}/assets/${clienteCase.content}`,
        type: clienteCase.type,
      };
    });
  }, [cases]);

  return (
    <>
      <SEO
        title="Aplicativos"
        description="Todos os aplicativos que sua transportadora precisa para ter uma plataforma completa e moderna no seu site, conectada ao sistema! Isto tudo com foco na usabilidade e experiência do usuário."
      />
      <Layout>
        <PageWrapper>
          <PageContainer>
            <SectionWrapper>
              <HeroSection />
            </SectionWrapper>
          </PageContainer>

          <NovosAplicativos />
          <Transportadoras
            empresas={empresas}
            title="Empresas que possuem aplicativos com a gente"
          />

          <Pontuacao />

          <PageContainer>
            <SectionWrapper>
              <Integracao />
            </SectionWrapper>
          </PageContainer>

          <PrincipaisCases
            iconURL="/icons/aplicativos-icon.svg"
            cases={casesParsed}
          />

          <PageContainer>
            <SectionWrapper>
              <ClientCases testemunhos={clientCases} />
            </SectionWrapper>

            <SectionWrapper>
              <ReusableBottomContent title="Consultoria gratuita">
                <p>
                  Nossos especialistas vão te atender sem compromisso para
                  entender as necessidades e expectativas da sua empresa.
                </p>
              </ReusableBottomContent>
            </SectionWrapper>
          </PageContainer>
        </PageWrapper>
      </Layout>
    </>
  );
};

export default Aplicativos;

export const getStaticProps: GetStaticProps = async () => {
  const directus = await DirectusInstance.getInstance();

  const { data: cases }: ManyItems<IWebAppCases> = await directus
    .items('apps_cases_slider')
    .readByQuery({
      limit: -1,
      fields: ['type', 'id', 'content'],
      filter: {
        status: { _in: ['published'] },
      },
    });

  const { data: clients }: ManyItems<IClients> = await directus
    .items('clients')
    .readByQuery({
      limit: -1,
      fields: ['logo', 'id', 'name', 'services'],
      filter: {
        status: { _in: ['published'] },
        is_active: { _eq: true },
      },
      sort: ['name' as never],
    });

  const clientes: IClients[] = [];

  clients?.forEach((client) => {
    if (client.services.indexOf('webapp') > -1) {
      clientes.push(client);
    }
  });

  return {
    props: { clientes, cases },
    revalidate: 100000,
  };
};
